import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { StandardStatisticsData } from '../../../models/statistics';
import { ChartDataset, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { differenceInMonths, parseISO } from 'date-fns';
import { TranslateService } from '@ngx-translate/core';

// Chart.js draws on a canvas and cannot read CSS variables, so the primary color that setColors()
// puts on :root (company settings) is resolved here.
function selectedColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue('--bs-primary').trim() || '#0d6efd';
}
const UNSELECTED_COLOR = '#d7d8d5';

interface GroupedRow {
  key: string;
  elements: StandardStatisticsData[];
  value?: number;
  count?: number;
}

export interface NewLayoutRow {
  key: string;
  label: string;
  value: number;
}

@Component({
    selector: 'lib-order-analytics-chart-table-horizontal',
    templateUrl: './order-analytics-chart-table-horizontal.component.html',
    styleUrls: ['./order-analytics-chart-table-horizontal.component.css'],
    standalone: false
})
export class OrderAnalyticsChartTableHorizontalComponent implements OnInit, OnChanges {
  @Input()
  data: StandardStatisticsData[];

  @Input()
  groupBy: string;

  @Input()
  tableOnly: boolean;

  @Input()
  heading: string;

  @Input()
  headerRows: string[];

  @Input()
  hideVerticalGridLines?: boolean = false;

  // Modern period-rows-with-a-single-selectable-amount layout, used by OrderAnalyticsStandaloneComponent
  // for the customer statistics page. Left false (the original behaviour, metrics-as-rows/no
  // selection) for every other consumer of this component (wiklunds/wiges/ivanhoe's own
  // *-order-analytics-standalone overrides, order-analytics-list) so none of them are affected.
  @Input()
  newTableLayout?: boolean = false;

  // The currently-selected period's key (e.g. a year). Undefined on first render selects the last
  // (most recent) period, matching the design reference's default state.
  @Input()
  selectedKey?: string;

  @Output()
  selectedKeyChange = new EventEmitter<string>();

  // Emitted alongside selectedKeyChange so the parent can show the big amount without duplicating
  // this component's own grouping/summing logic.
  @Output()
  selectedAmountChange = new EventEmitter<number>();

  barChartData: ChartDataset[];
  barChartLabels: BaseChartDirective['labels'] = [];
  barChartOptions: any = {}; // todo
  barChartLegend = false;
  barChartType: ChartType = 'bar';

  loading: boolean = true;

  tableHeaderRowData: string[] = [];
  tableRowData: string[][] = [];
  newLayoutRows: NewLayoutRow[] = [];

  private groupedDataCache: GroupedRow[] = [];

  constructor(protected translateService: TranslateService) { }

  ngOnInit() {
    this.barChartOptions = {
      backgroundColor: this.newTableLayout ? UNSELECTED_COLOR : 'rgba(206,212,218, 0.6)',
      hoverBackgroundColor: this.newTableLayout ? selectedColor() : 'rgba(206,212,218, 0.8)'
    };

    if (this.hideVerticalGridLines) {
      this.barChartOptions = {
        ...this.barChartOptions,
        scales: {
          ...this.barChartOptions?.scales,
          x: {
            ...this.barChartOptions?.scales?.x,
            grid: {
              ...this.barChartOptions?.scales?.x?.grid,
              display: false
            }
          }
        }
      };
    }

    if (this.newTableLayout) {
      this.barChartOptions = {
        ...this.barChartOptions,
        maintainAspectRatio: false,
        animation: { duration: 260 },
        scales: {
          ...this.barChartOptions?.scales,
          y: {
            ...this.barChartOptions?.scales?.y,
            beginAtZero: true,
            grid: { color: '#e6e6e4', drawTicks: false },
            border: { display: false }
          },
          x: {
            ...this.barChartOptions?.scales?.x,
            border: { display: false }
          }
        },
        plugins: {
          ...this.barChartOptions?.plugins,
          tooltip: {
            backgroundColor: '#222222',
            padding: 8,
            titleFont: { size: 11 },
            bodyFont: { size: 11 }
          }
        }
      };
    }

    this.rebuild();
  }

  ngOnChanges() {
    this.rebuild();
  }

  private rebuild(): void {
    const groupedData = this.groupData();
    this.groupedDataCache = groupedData;
    this.buildGraphData(groupedData);
    if (this.newTableLayout) {
      this.buildNewLayoutRows(groupedData);
    } else {
      this.buildTableData(groupedData);
    }
    this.loading = false;
  }

  buildGraphData(groupedData: GroupedRow[]) {
    const groupedDataValue = groupedData.map(element => {
      element.value = Math.round(
        element.elements.reduce((acc: number, curr: StandardStatisticsData) => {
          return acc + curr.orderRowTotalValueCurrency;
        }, 0)
      );
      element.count = Math.round(
        element.elements.reduce((acc: number, curr: StandardStatisticsData) => {
          return acc + curr.orderRowTotalQuantity;
        }, 0)
      );
      return element;
    });
    const chartData: number[] = [];
    const chartLabels: string[] = [];
    groupedDataValue.forEach(element => {
      chartData.push(element.value);
      if (this.heading === 'PRODUCT_CATEGORY' && element.key.toString() === 'null') {
        chartLabels.push(this.translateService.instant('ALL_CATEGORIES'));
      } else {
        chartLabels.push(element.key);
      }
    });

    if (this.newTableLayout) {
      if (!this.selectedKey && groupedDataValue.length > 0) {
        this.setSelected(groupedDataValue[groupedDataValue.length - 1]);
      }
      this.barChartData = [
        {
          data: chartData,
          backgroundColor: groupedDataValue.map(el => (el.key === this.selectedKey ? selectedColor() : UNSELECTED_COLOR)),
          hoverBackgroundColor: groupedDataValue.map(el => (el.key === this.selectedKey ? selectedColor() : UNSELECTED_COLOR)),
          borderRadius: 3,
          borderSkipped: false,
          maxBarThickness: 66
        }
      ];
    } else {
      this.barChartData = [
        {
          data: chartData
        }
      ];
    }
    this.barChartLabels = chartLabels;
  }

  buildTableData(groupedData: GroupedRow[]) {
    this.tableHeaderRowData = [];
    this.tableRowData = [];

    for (const [index, row] of this.headerRows.entries()) {
      if (index === 0) {
        this.tableHeaderRowData.push(row);
        for (let data of groupedData) {
          if (this.heading === 'PRODUCT_CATEGORY' && data.key.toString() === 'null') {
            this.tableHeaderRowData.push(this.translateService.instant('ALL_CATEGORIES'));
          } else {
            this.tableHeaderRowData.push(data.key.toString());
          }
        }
        continue;
      }

      const newDataRow: string[] = [];
      newDataRow.push(row);
      for (let data of groupedData) {
        if (row === 'SALES_AMOUNT' || row === 'SALES') {
          newDataRow.push((data as any)?.value || '0');
        } else if (row === 'QTY') {
          newDataRow.push((data as any)?.count || '0');
        } else if (row === 'NUMBER_OF_ORDERS') {
          newDataRow.push(this.getNumberOfOrders(data));
        }
      }

      this.tableRowData.push(newDataRow);
    }
  }

  // The modern layout: one row per period (year/quarter/month/...), not one row per metric - "Kolumner
  // År och Sålt värde, kr" in the design brief. headerRows[0] (e.g. 'YEAR') is reused as the period
  // column's own translated label. Newest period first (reverse of the chart's own left-to-right
  // oldest-to-newest axis, which groupedData is already sorted for) - a table reads top-to-bottom,
  // so the most relevant/recent row belongs at the top, not scrolled to the bottom.
  buildNewLayoutRows(groupedData: GroupedRow[]) {
    this.newLayoutRows = [...groupedData].reverse().map(data => ({
      key: data.key,
      label:
        this.heading === 'PRODUCT_CATEGORY' && data.key.toString() === 'null'
          ? this.translateService.instant('ALL_CATEGORIES')
          : data.key,
      value: data.value || 0
    }));
  }

  selectRow(key: string): void {
    const found = this.groupedDataCache.find(g => g.key === key);
    if (found) {
      this.setSelected(found);
      // Re-colour the chart without rebuilding the whole dataset, so Chart.js only animates the
      // colour transition instead of re-running the full geometry animation.
      this.buildGraphData(this.groupedDataCache);
    }
  }

  onChartClick(event: { event?: unknown; active?: { index: number }[] }): void {
    const index = event?.active?.[0]?.index;
    if (index === undefined || index === null) {
      return;
    }
    const found = this.groupedDataCache[index];
    if (found) {
      this.selectRow(found.key);
    }
  }

  private setSelected(row: GroupedRow): void {
    this.selectedKey = row.key;
    this.selectedKeyChange.emit(row.key);
    this.selectedAmountChange.emit(row.value || 0);
  }

  getNumberOfOrders(data: GroupedRow): string {
    const idArray: string[] = data?.elements?.map(e => e?.orderId);
    const uniqueIdsSet: Set<string> = new Set(idArray);
    return uniqueIdsSet.size.toString();
  }

  protected groupData(): GroupedRow[] {
    let filteredData = this.data;

    if (this.groupBy === 'productCategoryCode' || this.groupBy === 'productCategoryDescription') {
      const currentDate = new Date();
      filteredData = this.data.filter(item => {
        const orderCreationDate = parseISO(item.orderCreationDate);

        return differenceInMonths(currentDate, orderCreationDate) < 12;
      });
    }

    const result = [];
    const groupedData = filteredData.reduce((acc, curr) => {
      if (this.groupBy === 'orderCreationDateYear') {
        const quarterKey = 'orderCreationDateYearQuarter';
        const year = curr[quarterKey].split('-')[0];
        (acc[year] = acc[year] || []).push(curr);
      } else {
        (acc[curr[this.groupBy]] = acc[curr[this.groupBy]] || []).push(curr);
      }
      return acc;
    }, {});
    for (const key of Object.keys(groupedData)) {
      result.push({
        key,
        elements: groupedData[key]
      });
    }

    result.sort((p1, p2) => {
      const key1: string = p1.key;
      const key2: string = p2.key;
      // Rows with no value for the grouped field end up under the key "null" - keep that bucket
      // last instead of letting it sort alphabetically in among the real values.
      if (key1 === 'null' && key2 !== 'null') {
        return 1;
      }
      if (key2 === 'null' && key1 !== 'null') {
        return -1;
      }
      return key1.localeCompare(key2);
    });

    if (this.groupBy === 'orderCreationDateYearMonth') {
      const result12Months = [];
      for (const month of this.getLast12Months()) {
        result12Months.push({
          key: month,
          elements: []
        });
      }

      for (const res of result) {
        const index = result12Months.findIndex(r => r?.key === res?.key);
        if (index > -1) {
          result12Months.splice(index, 1, res);
        }
      }

      return result12Months;
    }
    return result;
  }

  getLast12Months() {
    const currentDate = new Date();
    const months = [];

    for (let i = 11; i >= 0; i--) {
      let month = currentDate.getMonth() - i;
      let year = currentDate.getFullYear();

      if (month < 0) {
        // Adjust for months in the previous year
        month += 12;
        year -= 1;
      }

      const monthString = String(month + 1).padStart(2, '0'); // Add leading zero if needed
      const yearString = String(year);

      const formattedDate = `${yearString}-${monthString}`;
      months.push(formattedDate);
    }

    return months;
  }

  isNotNumber(value: any): boolean {
    return isNaN(value);
  }
}
